import React, { useState, useEffect } from 'react';
import { 
  Search, MapPin, Calendar, Users, Loader,
  Building2, ChevronLeft, ChevronRight, Filter, X
} from 'lucide-react';
import { apartmentService } from '../../services/api';
import ApartmentCard from '../../components/ApartmentCard';
import { ApartmentSummary } from '../../types';
import toast from 'react-hot-toast';

interface City {
  name: string;
  count: number;
}

const ReservationSearchPage: React.FC = () => {
  // États des filtres
  const [ville, setVille] = useState('');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [nbPersonnes, setNbPersonnes] = useState<number | ''>('');
  
  // États des données
  const [cities, setCities] = useState<City[]>([]);
  const [apartments, setApartments] = useState<ApartmentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Mobile filter visibility
  const [showFilters, setShowFilters] = useState(true);

  // Charger les villes et tous les appartements au démarrage
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [citiesRes, apartmentsRes] = await Promise.all([
          apartmentService.getCities(),
          apartmentService.searchAvailable({ page: 1, per_page: 12 })
        ]);

        if (citiesRes.data?.success === false) {
          throw new Error(citiesRes.data?.error || 'Erreur lors du chargement des villes');
        }
        if (apartmentsRes.data?.success === false) {
          throw new Error(apartmentsRes.data?.error || 'Erreur lors du chargement des appartements');
        }

        const apartmentsList = apartmentsRes.data?.apartments ?? apartmentsRes.data?.items ?? [];
        setCities(citiesRes.data?.cities || []);
        setApartments(apartmentsList);
        setTotal(apartmentsRes.data?.total || 0);
        setTotalPages(apartmentsRes.data?.pages || 1);
      } catch (error) {
        console.error('Erreur chargement données:', error);
        toast.error('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // Fonction de recherche
  const handleSearch = async (e?: React.FormEvent, newPage: number = 1) => {
    if (e) e.preventDefault();
    
    // Validation des dates
    if (dateDebut && dateFin && new Date(dateDebut) >= new Date(dateFin)) {
      toast.error('La date de départ doit être après la date d\'arrivée');
      return;
    }
    
    setSearching(true);
    setHasSearched(true);
    setPage(newPage);
    
    try {
      const params: any = {
        page: newPage,
        per_page: 12
      };
      
      if (ville) params.ville = ville;
      if (dateDebut) params.date_debut = dateDebut;
      if (dateFin) params.date_fin = dateFin;
      if (nbPersonnes) params.nb_personnes = nbPersonnes;
      
      const response = await apartmentService.searchAvailable(params);

      if (response.data?.success === false) {
        throw new Error(response.data?.error || 'Erreur lors de la recherche');
      }

      const apartmentsList = response.data?.apartments ?? response.data?.items ?? [];
      setApartments(apartmentsList);
      setTotal(response.data?.total || 0);
      setTotalPages(response.data?.pages || 1);
      
      // Cacher les filtres sur mobile après recherche
      if (window.innerWidth < 768) {
        setShowFilters(false);
      }
    } catch (error: any) {
      console.error('Erreur recherche:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de la recherche');
    } finally {
      setSearching(false);
    }
  };

  // Réinitialiser les filtres
  const handleReset = () => {
    setVille('');
    setDateDebut('');
    setDateFin('');
    setNbPersonnes('');
    setHasSearched(false);
    setPage(1);
    
    // Recharger tous les appartements
    apartmentService.searchAvailable({ page: 1, per_page: 12 })
      .then(response => {
        if (response.data?.success === false) {
          throw new Error(response.data?.error || 'Erreur lors du rechargement');
        }
        const apartmentsList = response.data?.apartments ?? response.data?.items ?? [];
        setApartments(apartmentsList);
        setTotal(response.data?.total || 0);
        setTotalPages(response.data?.pages || 1);
      })
      .catch((error: any) => {
        console.error('Erreur rechargement:', error);
        toast.error(error?.message || 'Erreur lors du rechargement');
      });
  };

  // Pagination
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      handleSearch(undefined, newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Date minimum (aujourd'hui)
  const today = new Date().toISOString().split('T')[0];

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
            <Search className="w-6 h-6 text-primary-500" />
            Réservation
          </h1>
          <p className="text-neutral-500 mt-1">
            Trouvez l'appartement idéal pour votre séjour
          </p>
        </div>
        
        {/* Toggle filtres mobile */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="md:hidden btn btn-outline btn-sm flex items-center gap-2"
        >
          {showFilters ? <X className="w-4 h-4" /> : <Filter className="w-4 h-4" />}
          Filtres
        </button>
      </div>

      {/* Formulaire de recherche */}
      <div className={`card p-6 ${showFilters ? 'block' : 'hidden md:block'}`}>
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Ville */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                <MapPin className="w-4 h-4 inline mr-1" />
                Ville
              </label>
              <select
                value={ville}
                onChange={(e) => setVille(e.target.value)}
                className="input w-full"
              >
                <option value="">Toutes les villes</option>
                {cities.map((city) => (
                  <option key={city.name} value={city.name}>
                    {city.name} ({city.count} appartement{city.count > 1 ? 's' : ''})
                  </option>
                ))}
              </select>
            </div>

            {/* Date d'arrivée */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Date d'arrivée
              </label>
              <input
                type="date"
                value={dateDebut}
                onChange={(e) => setDateDebut(e.target.value)}
                min={today}
                className="input w-full"
              />
            </div>

            {/* Date de départ */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Date de départ
              </label>
              <input
                type="date"
                value={dateFin}
                onChange={(e) => setDateFin(e.target.value)}
                min={dateDebut || today}
                className="input w-full"
              />
            </div>

            {/* Nombre de personnes */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                <Users className="w-4 h-4 inline mr-1" />
                Nombre de personnes
              </label>
              <input
                type="number"
                value={nbPersonnes}
                onChange={(e) => setNbPersonnes(e.target.value ? parseInt(e.target.value) : '')}
                min="1"
                max="20"
                placeholder="Ex: 2"
                className="input w-full"
              />
            </div>
          </div>

          {/* Boutons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="submit"
              disabled={searching}
              className="btn btn-primary flex-1 sm:flex-none flex items-center justify-center gap-2"
            >
              {searching ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
              Rechercher
            </button>
            
            {hasSearched && (
              <button
                type="button"
                onClick={handleReset}
                className="btn btn-outline flex items-center justify-center gap-2"
              >
                <X className="w-4 h-4" />
                Réinitialiser
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Résumé des filtres actifs */}
      {hasSearched && (dateDebut || dateFin || ville || nbPersonnes) && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-neutral-500">Filtres actifs:</span>
          {ville && (
            <span className="bg-primary-100 text-primary-700 px-2 py-1 rounded-full">
              {ville}
            </span>
          )}
          {dateDebut && (
            <span className="bg-primary-100 text-primary-700 px-2 py-1 rounded-full">
              Du {new Date(dateDebut).toLocaleDateString('fr-FR')}
            </span>
          )}
          {dateFin && (
            <span className="bg-primary-100 text-primary-700 px-2 py-1 rounded-full">
              Au {new Date(dateFin).toLocaleDateString('fr-FR')}
            </span>
          )}
          {nbPersonnes && (
            <span className="bg-primary-100 text-primary-700 px-2 py-1 rounded-full">
              {nbPersonnes} personne{Number(nbPersonnes) > 1 ? 's' : ''}
            </span>
          )}
        </div>
      )}

      {/* Résultats */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-neutral-800">
            {hasSearched ? (
              <>
                {total} appartement{total > 1 ? 's' : ''} disponible{total > 1 ? 's' : ''}
              </>
            ) : (
              <>
                Tous les appartements ({total})
              </>
            )}
          </h2>
        </div>

        {apartments.length === 0 ? (
          <div className="card p-12 text-center">
            <Building2 className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-neutral-600 mb-2">
              Aucun appartement trouvé
            </h3>
            <p className="text-neutral-500 mb-4">
              {hasSearched 
                ? 'Essayez de modifier vos critères de recherche'
                : 'Aucun appartement disponible pour le moment'}
            </p>
            {hasSearched && (
              <button onClick={handleReset} className="btn btn-primary">
                Voir tous les appartements
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Grille d'appartements */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {apartments.map((apartment) => (
                <ApartmentCard
                  key={apartment.id}
                  apartment={apartment}
                  to={`/apartments/${apartment.id}?${new URLSearchParams({
                    ...(dateDebut ? { date_debut: dateDebut } : {}),
                    ...(dateFin ? { date_fin: dateFin } : {}),
                    ...(nbPersonnes ? { nb_personnes: String(nbPersonnes) } : {})
                  }).toString()}`}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1 || searching}
                  className="btn btn-outline btn-sm"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      pageNum = i + 1;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        disabled={searching}
                        className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                          page === pageNum
                            ? 'bg-primary-500 text-white'
                            : 'bg-neutral-100 hover:bg-neutral-200 text-neutral-700'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === totalPages || searching}
                  className="btn btn-outline btn-sm"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ReservationSearchPage;
